/**
 * Plain-English vocabulary.
 *
 * Everything a dispatcher, warehouse supervisor or lumper reads should sound
 * like something they would say out loud on a dock. No system words, no
 * accounting words, no jargon. When you add a screen, take the wording from
 * here instead of inventing new labels.
 */

/** What a job's status means, in words a lumper or receiver would use. */
export const JOB_STEPS = [
  { key: 'draft', plain: 'Not posted yet', short: 'Not posted', tone: 'muted', means: 'Saved, but not sent out to anyone.' },
  { key: 'posted', plain: 'Needs someone', short: 'Open', tone: 'amber', means: 'Nobody has taken this job yet.' },
  { key: 'assigned', plain: 'Someone is coming', short: 'Covered', tone: 'blue', means: 'A lumper has taken the job.' },
  { key: 'en_route', plain: 'On the way', short: 'On the way', tone: 'violet', means: 'The lumper said they are driving over.' },
  { key: 'checked_in', plain: 'At the dock', short: 'At dock', tone: 'violet', means: 'The lumper has arrived and checked in.' },
  { key: 'in_progress', plain: 'Unloading now', short: 'Unloading', tone: 'lime', means: 'Work has started on the trailer.' },
  { key: 'completed', plain: 'Work finished', short: 'Finished', tone: 'lime', means: 'The lumper says the trailer is done. Check the count.' },
  { key: 'verified', plain: 'Count checked', short: 'Counted', tone: 'teal', means: 'You confirmed the pallet count. Ready to pay.' },
  { key: 'paid', plain: 'Lumper paid', short: 'Paid', tone: 'green', means: 'The lumper has been paid for this job.' },
  { key: 'invoiced', plain: 'Customer billed', short: 'Billed', tone: 'green', means: 'The customer has been invoiced for this job.' },
  { key: 'cancelled', plain: 'Cancelled', short: 'Cancelled', tone: 'red', means: 'This job is not happening.' },
];

const JOB_BY_KEY = Object.fromEntries(JOB_STEPS.map((step) => [step.key, step]));

export const jobStep = (key) =>
  JOB_BY_KEY[key] || { key, plain: key, short: key, tone: 'muted', means: '' };

export const jobPlain = (key) => jobStep(key).plain;
export const jobShort = (key) => jobStep(key).short;
export const jobTone = (key) => jobStep(key).tone;
export const jobMeans = (key) => jobStep(key).means;
export const jobStatusKeys = JOB_STEPS.map((step) => step.key);

/** The one thing to do next on a job. */
export const JOB_NEXT_STEP = {
  draft: { to: 'posted', label: 'Send it out for help' },
  posted: { to: 'assigned', label: 'Give it to someone' },
  assigned: { to: 'en_route', label: 'They are on the way' },
  en_route: { to: 'checked_in', label: 'They arrived' },
  checked_in: { to: 'in_progress', label: 'Start the unload' },
  in_progress: { to: 'completed', label: 'The work is done' },
  completed: { to: 'verified', label: 'Count is right' },
  verified: { to: 'paid', label: 'We paid the lumper' },
  paid: { to: 'invoiced', label: 'We billed the customer' },
};

/** Where a person is in the sign-up process. */
export const LUMPER_STEPS = [
  { key: 'applied', plain: 'Just applied', short: 'New', tone: 'amber', means: 'They filled in the form and we have not looked yet.' },
  { key: 'screening', plain: 'Checking them out', short: 'Checking', tone: 'blue', means: 'Chasing paperwork and references.' },
  { key: 'approved', plain: 'Approved', short: 'Approved', tone: 'teal', means: 'Cleared to work, no jobs yet.' },
  // Not "Working": that word belongs to a job. This is the roster status — he is
  // on the list and will take work — and a dispatcher reading "Working" next to
  // a man with no job today is being told something untrue. The owner's wording:
  // "Available to work", with "Available" where there is no room for it.
  { key: 'active', plain: 'Available to work', short: 'Available', tone: 'green', means: 'On the roster and open to offers.' },
  { key: 'paused', plain: 'Taking a break', short: 'Paused', tone: 'muted', means: 'Not taking jobs for now.' },
  { key: 'suspended', plain: 'Stopped', short: 'Stopped', tone: 'red', means: 'Off the roster. Do not send work.' },
  { key: 'rejected', plain: 'Not taken on', short: 'No', tone: 'red', means: 'We turned this application down.' },
];

const LUMPER_BY_KEY = Object.fromEntries(LUMPER_STEPS.map((step) => [step.key, step]));
export const lumperStep = (key) =>
  LUMPER_BY_KEY[key] || { key, plain: key, short: key, tone: 'muted', means: '' };
export const lumperPlain = (key) => lumperStep(key).plain;
export const lumperTone = (key) => lumperStep(key).tone;

/**
 * One-line explanations, used as the small grey line under a heading so nobody
 * has to ask what a number means.
 */
export const MEANS = {
  fillRate: 'Out of every 100 jobs, how many got someone to do them.',
  margin: 'What is left over after the lumper is paid.',
  billed: 'What we charged the customers.',
  payToLumpers: 'What we paid the people who did the work.',
  leftOver: 'Money left after the lumpers are paid.',
  unpaid: 'Invoices the customers have not paid yet.',
  palletsMoved: 'How many pallets the crews have unloaded.',
  onTime: 'How often they turn up when they said they would.',
  waitingToPay: 'Finished jobs the lumpers have not been paid for yet.',
  dockWait: 'How long a crew sat waiting before they could start.',
  unloadTime: 'How long the unload itself took, on average.',
  needsSomeone: 'Jobs with nobody lined up yet.',
  freeNow: 'Lumpers with nothing on right now, ready to take work.',
  paperwork: 'Forms and cards that working lumpers have to keep up to date.',
};

/** Friendly wording for what is inside the trailer. */
export const FREIGHT_PLAIN = {
  floor_loaded: 'Loose boxes, stacked to the roof',
  pallets: 'On pallets',
  mixed_pallets: 'Mixed pallets',
  produce: 'Fruit and vegetables',
  cases: 'Loose boxes',
  cross_dock: 'Moving between trailers',
  returns: 'Returns',
  live_load: 'Straight onto another truck',
};

export const WORK_PLAIN = {
  unload: 'Unload the trailer',
  load: 'Load the trailer',
  sort: 'Sort it out',
  cross_dock: 'Move to another trailer',
  putaway: 'Put it on the racks',
  rework: 'Re-do and re-label',
};

export const TEMP_PLAIN = {
  ambient: 'Dry trailer',
  reefer: 'Cold trailer',
  frozen: 'Frozen trailer',
};

/**
 * What is coming off the trailer, in words a lumper would use.
 *
 * Most of this work is containers, not pallets, and a container job that reads
 * "0 pallets" is worse than useless — it tells a man nothing about the job he is
 * deciding on. One phrase, used everywhere, so the office, the phone and the
 * paperwork all say the same thing.
 */
export const freightPlain = (job) => {
  const containers = Number(job?.containersExpected || 0);
  const size = job?.containerSize ? String(job.containerSize) : '';
  if (containers > 0 || size) {
    const count = containers > 0 ? containers : 1;
    const what = size ? `${count} × ${size} container` : countOf(count, 'container');
    return size && count > 1 ? `${what}s` : what;
  }
  const pallets = Number(job?.palletsExpected || 0);
  if (!pallets) return 'Amount not noted';
  return countOf(pallets, 'pallet');
};

/** Money in plain dollars — never "$21K". */
export const dollars = (value, { cents = false } = {}) => {
  const n = Number(value || 0);
  const body = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
  return `${n < 0 ? '-' : ''}$${body}`;
};

/** "1 job" / "4 jobs" without anyone having to think. */
export const plural = (count, one, many = null) => {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? one : many || `${one}s`}`;
};

export const countOf = (count, one, many = null) => {
  const n = Number(count) || 0;
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many || `${one}s`}`;
};

/** Percentages the way people say them. */
export const outOfTen = (percent) => `${Math.round((Number(percent) || 0) / 10)} out of 10`;

/** "Today at 4:30 PM" instead of a timestamp. */
export const whenPlain = (date) => {
  if (!date) return 'No time set';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return 'No time set';
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 864e5);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `Today at ${time}`;
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow at ${time}`;
  return `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${time}`;
};

/** How far away, in words. */
export const distancePlain = (miles) => {
  const n = Number(miles || 0);
  if (!n) return 'Not sure how far';
  if (n < 1) return 'Very close by';
  if (n < 15) return `${Math.round(n)} miles away`;
  if (n < 40) return `${Math.round(n)} miles, a bit of a drive`;
  return `${Math.round(n)} miles, quite far`;
};

/**
 * A rough length of time in words: "about 3 hours", never "2h 36m".
 * Estimates are rounded to the nearest half hour because that is how people
 * actually talk about a job.
 */
export const durationPlain = (hours) => {
  const n = Number(hours || 0);
  if (!n) return 'no time estimate';
  if (n < 0.75) return 'about 45 minutes';
  if (n < 1.25) return 'about an hour';
  if (n < 1.75) return 'about an hour and a half';
  const rounded = Math.round(n * 2) / 2;
  return `about ${rounded % 1 ? rounded.toFixed(1) : rounded} hours`;
};

/**
 * The fit between a person and a job, in words. The score itself is never
 * shown to anyone — most people would not know what to do with a number.
 */
export const matchWords = (score) => {
  const n = Number(score) || 0;
  if (n >= 45) return { label: 'Really good match', tone: 'green' };
  if (n >= 32) return { label: 'Good match', tone: 'teal' };
  if (n >= 20) return { label: 'Could work', tone: 'amber' };
  return { label: 'Last resort', tone: 'muted' };
};
